import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { AddressesService } from '@/api/services/AddressesService';
import type { Address } from '@/types/address';

export function useAddress(id?: string) {
  return useQuery({
    queryKey: ['address', id],
    queryFn: () => id ? AddressesService.getApiV1Addresses1(id) : null,
    enabled: !!id,
  });
}

/**
 * Fetch addresses with server-side full-field search. The backend ILIKEs across
 * street, house number, info line, zip, city, and country, so large-list
 * address dropdowns stay correct regardless of tenant size — there is no
 * client-side cutoff. The org id and search term are part of the query key so a
 * user switching orgs can't read another tenant's cached rows and
 * `keepPreviousData` never serves a stale term.
 */
export function useAddressesSearch(search: string, limit = 50) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  const trimmed = search.trim();

  return useQuery<Address[]>({
    queryKey: ['addresses', 'search-all', { orgId, search: trimmed, limit }],
    queryFn: () => AddressesService.getApiV1Addresses(limit, 0, trimmed || undefined),
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    // Keep the previous results visible while a new term resolves so the list
    // doesn't flash empty on every keystroke.
    placeholderData: (previous) => previous,
  });
}

export function useAddresses(limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['addresses', limit, offset],
    queryFn: () => AddressesService.getApiV1Addresses(limit, offset),
  });
}

export function useAddressSearch(city: string, limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['addresses', 'search', city, limit, offset],
    queryFn: () => AddressesService.getApiV1AddressesSearch(city, limit, offset),
    enabled: !!city && city.length >= 2,
  });
}